import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink, type To } from 'react-router-dom';

import { CLIENT_BRAND_NAME } from '../branding';
import { usePageHeaderPortalTarget } from './PageHeaderPortal';

export type PageHeaderBackLink = {
  label: string;
  to: To;
  state?: unknown;
};

export type PageHeaderProps = {
  title: string;
  subtitle?: ReactNode;
  backLink?: PageHeaderBackLink;
  metadata?: ReactNode;
  action?: ReactNode;
};

type PageHeaderContentProps = PageHeaderProps & {
  inPortal: boolean;
};

const PageHeaderContent = ({ title, subtitle, backLink, metadata, action, inPortal }: PageHeaderContentProps) => (
  <Stack
    data-testid="page-header-content"
    direction="row"
    useFlexGap
    sx={{
      width: '100%',
      minWidth: 0,
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      columnGap: 2,
      rowGap: 1,
      mb: inPortal ? 0 : 4,
    }}
  >
    <Box sx={{ minWidth: 0, flex: '1 1 12rem' }}>
      {backLink && (
        <MuiLink
          component={RouterLink}
          to={backLink.to}
          state={backLink.state}
          underline="hover"
          color="inherit"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
        >
          <ArrowBackIcon fontSize="inherit" aria-hidden="true" />
          {backLink.label}
        </MuiLink>
      )}
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle !== undefined && subtitle !== null && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
      {metadata !== undefined && metadata !== null && <Box sx={{ mt: 0.5 }}>{metadata}</Box>}
    </Box>
    {action !== undefined && action !== null && <Box sx={{ minWidth: 0, maxWidth: '100%', flex: '0 1 auto' }}>{action}</Box>}
  </Stack>
);

const useActivePageHeaderTitle = (title: string, isActive: boolean): void => {
  useEffect(() => {
    if (!isActive || typeof document === 'undefined') return undefined;

    const previousTitle = document.title;
    document.title = `${title} | ${CLIENT_BRAND_NAME}`;

    return () => {
      document.title = previousTitle;
    };
  }, [isActive, title]);
};

export const PageHeader = (props: PageHeaderProps) => {
  const target = usePageHeaderPortalTarget();
  useActivePageHeaderTitle(props.title, target !== null);

  const content = <PageHeaderContent {...props} inPortal={target !== null} />;
  return target === null ? content : createPortal(content, target);
};
